import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Grid2,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useBaseEntityIdLabel } from "../../../../Hooks/usePagesContent";
import CustomIcon from "../../../../Components/CustomIcon";
import CloseButton from "../../../../Components/CloseButton";
import { PrimaryTooltip } from "../../../../Components/StyledComponent";
import { getMetricTimeStamp } from "../../../../Helper/DatePicker/DateConverter";
import BatteryDeviceInfoMap from "./BatteryDeviceInfoMap";
import BusIcon from "../../../../Assets/Icons/Map/Icons/Bus.png"
import TowerAlert from "../../../../Assets/Icons/Map/Icons/Tower_bms_error.png";
import BNextTowerAlert from "../../../../Assets/Icons/Map/Icons/bnext_tower_bms_error.png";
import { formatSocValue } from "../../../../Helper/DigitalTwin/DigitalTwinHelper";
import { normalizeKey } from "../../../../Helper/Common/common_helpers";
import { useBatteryInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import Cookies from "universal-cookie";
import DotSeparator from "../../../../Components/DotSeparator";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    width: "762px",
    maxWidth: "90%",
  },
}));

const orgMarkerIcons = {
  default: TowerAlert,
  jbm: BusIcon,
  bnext: BNextTowerAlert,
};

const BatteryDeviceInfoMapDialog = ({vehicleInfo = {}, batteryTags }) => {

  const cookies = new Cookies();
  const orgName = cookies.get("orgName");

  const {t} = useTranslation(['common', 'digital_twin']);
  // Plate label: globalConfig.baseEntity.idLabel, else "Bus ID".
  const vehicleIdLabel = useBaseEntityIdLabel(t("digital_twin:bus_id"));

  const {batteryInfo, showBusDeviceInfo} = useBatteryInfo();

  const [batteryGeoLocation, setBatteryGeoLocation] = useState({});
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
  };

  const getMarkerIcon = () => {
    if (showBusDeviceInfo()) {
      return BusIcon;
    }
    return orgMarkerIcons[orgName];
  }

  useEffect(() => {
    setBatteryGeoLocation({
      'latitude': parseFloat(batteryInfo.latitude),
      'longitude': parseFloat(batteryInfo.longitude),
      'location': batteryInfo.location,
    });
  }, [batteryInfo])

  return (
    <>
      <Box onClick={handleOpen} sx={{'&:hover': {cursor: 'pointer'}}}>
        <CustomIcon name='map-icon' />
      </Box>

      <StyledDialog open={open} onClose={handleClose} sx={{ p: 3 }}>
        <DialogContent sx={{py: 4}}>
          <Grid2 sx={{px: 2, mb: 3}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
              <Typography variant="fw500s20c000000" sx={{ lineHeight: '24px' }}>{t("common:map")}</Typography>
              <CloseButton onClick={handleClose} />
            </Box>
            <Box sx={{mb: 1}}>
              {showBusDeviceInfo() && vehicleInfo.licensePlateNo ? (
                <>
                  <Typography variant="fw400s16c000000">{vehicleIdLabel}:&nbsp;</Typography>
                  <Typography variant="fw500s16c000000">{vehicleInfo.licensePlateNo}</Typography>
                </>
              ) : (
                <>
                  <Typography variant="fw400s16c000000">{t('common:device_id')}:&nbsp;</Typography>
                  <Typography variant="fw500s16c000000">{batteryInfo.deviceID}</Typography>
                </>
              )}
            </Box>
            <Grid2 sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Box>
                <Typography variant="fw400s14c8F8F8F" sx={{lineHeight: '22px'}}>{t('digital_twin:last_pinged')}:&nbsp;</Typography>
                <Typography variant="fw400s14c000000">{t(`common:battery_status.${normalizeKey(batteryInfo.batteryStatus)}`, batteryInfo.batteryStatus)}&nbsp;-&nbsp;</Typography>
                {batteryInfo.lastGpsUpdatedTime ?
                  <PrimaryTooltip title={getMetricTimeStamp(batteryInfo.lastGpsUpdatedTime)} placement="bottom">
                    <Typography variant="fw400s14c000000">{getMetricTimeStamp(batteryInfo.lastGpsUpdatedTime, 'relative')}</Typography>
                  </PrimaryTooltip> : <Typography variant="fw400s14c000000">NA</Typography>
                }
              </Box>
              <DotSeparator />
              <Box>
                <Typography variant="fw400s14c8F8F8F">{t('digital_twin:soc')}:&nbsp;</Typography>
                <Typography variant="fw400s14c000000">{`${formatSocValue(batteryTags?.soc.value)} %`}</Typography>
              </Box>
            </Grid2>
          </Grid2>
          <BatteryDeviceInfoMap batteryGeoLocation={batteryGeoLocation} markerIcon={getMarkerIcon()} />
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default BatteryDeviceInfoMapDialog;
